import React, { useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider, useTheme } from './context/ThemeContext';

// Components
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ServicesSection from './components/ServicesSection';
import PackagesSection from './components/PackagesSection';
import TestimonialsSection from './components/TestimonialsSection';
import Footer from './components/Footer';
import ChatBot from './components/ChatBot';
import VoiceChatBot from './components/VoiceChatBot';
import FloatingChatButton from './components/FloatingChatButton';
import BlinkingRobot from './components/BlinkingRobot';
// FreeWebsitePopup removed per user request
import AdminDashboard from './components/AdminDashboard';
import ClientPortal from './components/ClientPortal';
import AdminBackend from './components/AdminBackend';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import CookiePolicy from './components/CookiePolicy';
import DataRequest from './components/DataRequest';
import { PaymentSuccess, PaymentCancelled } from './components/PaymentPages';
import VisitorTracker from './components/VisitorTracker';
import CookieConsent from './components/CookieConsent';
import CookiePreferencesButton from './components/CookiePreferencesButton';
import CaseStudiesPage from './components/CaseStudiesPage';
import AuthCallback from './components/AuthCallback';
import ReviewsPage from './components/ReviewsPage';

const HomePage = () => {
  const { isDarkMode } = useTheme();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isVoiceChatOpen, setIsVoiceChatOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isClientPortalOpen, setIsClientPortalOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  
  // Customer auth state
  const [customerData, setCustomerData] = useState(null);

  // Auto-open chatbot after 4 seconds for first-time visitors (desktop only)
  React.useEffect(() => {
    const hasSeenChat = sessionStorage.getItem('neura_chat_shown');
    // Only auto-open on desktop (screens wider than 768px)
    const isDesktop = window.innerWidth >= 768;
    if (!hasSeenChat && isDesktop) {
      const timer = setTimeout(() => {
        setIsChatOpen(true);
        sessionStorage.setItem('neura_chat_shown', 'true');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, []);
  
  // Handle customer login from AuthModal
  const handleCustomerLogin = (userData) => {
    setCustomerData(userData);
    // Store in session for persistence
    sessionStorage.setItem('neura_customer', JSON.stringify(userData));
  };
  
  // Check for existing customer session on mount
  React.useEffect(() => {
    const savedCustomer = sessionStorage.getItem('neura_customer');
    if (savedCustomer) {
      try {
        setCustomerData(JSON.parse(savedCustomer));
      } catch (e) {
        sessionStorage.removeItem('neura_customer');
      }
    }
  }, []);

  const handleBookNow = () => {
    setIsChatOpen(true);
  };

  const handleOpenChat = () => {
    setIsChatOpen(true);
  };

  const handleCloseVoiceChat = () => {
    setIsVoiceChatOpen(false);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      <Header 
        onBookNow={handleBookNow} 
        onOpenAdmin={() => setIsAdminOpen(true)}
        onOpenClientPortal={() => setIsClientPortalOpen(true)}
        onCustomerLogin={handleCustomerLogin}
      />
      <main>
        <HeroSection onBookNow={handleBookNow} />
        <PackagesSection onBookNow={handleBookNow} />
        <ServicesSection onBookNow={handleBookNow} />
        <AboutSection />
        <TestimonialsSection />
      </main>
      <Footer onBookNow={handleBookNow} />
      
      {/* Blinking Robot Chat (Left) */}
      <BlinkingRobot onClick={handleOpenChat} />
      
      {/* Text Chat Bot */}
      <ChatBot 
        isOpen={isChatOpen} 
        onClose={() => setIsChatOpen(false)}
        selectedBusiness={selectedBusiness}
      />

      {/* Voice Chat Bot for Booking */}
      <VoiceChatBot
        isOpen={isVoiceChatOpen}
        onClose={handleCloseVoiceChat}
        selectedBusiness={selectedBusiness}
      />

      {/* Admin Dashboard (Privacy-First: Only Client IDs) */}
      <AdminDashboard
        isOpen={isAdminOpen}
        onClose={() => setIsAdminOpen(false)}
      />

      {/* Client Login Portal */}
      <ClientPortal
        isOpen={isClientPortalOpen}
        onClose={() => setIsClientPortalOpen(false)}
      />
    </div>
  );
};

// Admin Backend Portal Page (Standalone)
const AdminBackendPage = () => {
  return <AdminBackend isOpen={true} onClose={() => window.location.href = '/'} />;
};

function AppRouter() {
  const location = window.location;
  
  // Check URL fragment for session_id (OAuth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/case-studies" element={<CaseStudiesPage />} />
      <Route path="/reviews" element={<ReviewsPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/data-request" element={<DataRequest />} />
      <Route path="/backend" element={<AdminBackendPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancelled" element={<PaymentCancelled />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <VisitorTracker />
        <CookieConsent />
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
